import React, { useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import styled, { css } from 'styled-components';
import { Box } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { spaces } from '@99designs/emu/settings';
import { LeftButton, RightButton, buttonHoverColor } from './ScrollButtons';

interface ScrollProps {
    children: React.ReactNode;
    gridWidth?: number;
    controlsEnabled?: boolean;
}

const Wrapper = styled.div`
    overflow: hidden;
    position: relative;
    display: flex;
    justify-content: center;
`;

const WrapperInner = styled.div`
    display: flex;
    flex-direction: row;
    white-space: nowrap;
    overflow-y: hidden;
    position: relative;
    width: 100%;

    /* Default state, will be re-set in useEffect based on system scrollbar settings */
    margin-top: 0;
    bottom: -20px;
    padding-bottom: 20px;
`;

const ContentWrapper = styled.div<{ maxWidth?: number }>(
    ({ maxWidth = 0 }) => css`
        display: flex;
        &::before,
        &::after {
            content: '';
            display: block;
            flex-shrink: 0;
            width: ${spaces.px['1bu']}px;

            ${mediaQuery.only('small')} {
                width: ${spaces.px['1bu']}px;
            }
        }

        ${maxWidth > 0 &&
        css`
            max-width: ${maxWidth}px;
            margin-left: auto;
            margin-right: auto;
        `}
    `
);

const sharedButtonStyles = css`
    position: absolute;
    top: 0;
    bottom: 0;
    margin: auto;

    ${Wrapper}:hover & {
        background-color: ${buttonHoverColor};
    }
`;

const StyledLeftButton = styled(LeftButton)`
    ${sharedButtonStyles}
    left: 0;
    margin-left: -6px;
`;

const StyledRightButton = styled(RightButton)`
    ${sharedButtonStyles}
    right: 0;
    margin-right: -6px;
`;

const getScrollWidth = (innerWidth: number, gridWidth?: number) => {
    const scrollWidthPercentage = 0.7;

    if (!gridWidth || innerWidth < gridWidth) {
        return innerWidth * scrollWidthPercentage;
    }
    return gridWidth * scrollWidthPercentage;
};

export const scrollElement = (
    scrollInnerRef: React.RefObject<HTMLDivElement>,
    scrollAmount: number
) => {
    if (scrollInnerRef.current) {
        // some browsers require the smoothscroll-polyfill for smooth scrolling
        scrollInnerRef.current.scrollBy({
            left: scrollAmount,
            behavior: 'smooth',
        });
    }
};

export const Scroll = ({ children, controlsEnabled, gridWidth }: ScrollProps) => {
    const scrollInnerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [containerStyles, setContainerStyles] = useState({});
    const [showControls, setShowControls] = useState({ right: false, left: false });
    const [scrollWidth, setScrollWidth] = useState(0);

    const scrollLeft = () => {
        scrollElement(scrollInnerRef, -scrollWidth);
    };
    const scrollRight = () => {
        scrollElement(scrollInnerRef, scrollWidth);
    };

    const setControlsVisibility = () => {
        const element = scrollInnerRef.current;

        if (element) {
            const scrollMax = element.scrollWidth - element.clientWidth;
            const scrollLeft = element.scrollLeft;

            setShowControls({
                right: scrollLeft < scrollMax,
                left: scrollLeft > 0,
            });
        }
    };

    useEffect(() => {
        const setContainerScrollWidth = () => {
            setScrollWidth(getScrollWidth(window.innerWidth, gridWidth));
        };

        const onWindowResize = () => {
            setContainerScrollWidth();
            setControlsVisibility();
        };

        if (scrollRef.current && scrollInnerRef.current) {
            const scrollbarHeight =
                scrollRef.current.clientHeight - scrollInnerRef.current.clientHeight;

            if (scrollbarHeight > 0) {
                setContainerStyles({
                    bottom: -1 * scrollbarHeight,
                    marginTop: -1 * scrollbarHeight,
                    paddingBottom: 0,
                });
            }

            if (!gridWidth) {
                center(scrollInnerRef.current);
            }

            if (controlsEnabled) {
                // Initialise scroll width and controls visibility
                setContainerScrollWidth();
                setControlsVisibility();

                // Set controls display on scroll
                scrollInnerRef.current.addEventListener(
                    'scroll',
                    debounce(setControlsVisibility, 100)
                );

                // Update scroll width on resize
                window.addEventListener('resize', debounce(onWindowResize, 100));
            }
        }
        return () => window.removeEventListener('resize', debounce(onWindowResize, 100));
    }, [controlsEnabled, gridWidth]);

    return (
        <Wrapper ref={scrollRef}>
            {controlsEnabled && (
                <>
                    <StyledLeftButton onClick={scrollLeft} disabled={!showControls.left} />
                    <StyledRightButton onClick={scrollRight} disabled={!showControls.right} />
                </>
            )}
            <WrapperInner ref={scrollInnerRef} style={containerStyles}>
                <ContentWrapper maxWidth={gridWidth ? gridWidth : 0}>
                    <Box flexShrink={0} whiteSpace="normal">
                        {children}
                    </Box>
                </ContentWrapper>
            </WrapperInner>
        </Wrapper>
    );
};

function center(element: HTMLDivElement) {
    element.scrollLeft = (element.scrollWidth - element.clientWidth) / 2;
}
